import { Children, isValidElement, type ReactElement, type ReactNode } from 'react';
import { Alert as ShadcnAlert, AlertDescription } from './ui/alert';
import { Badge as ShadcnBadge } from './ui/badge';
import { Button as ShadcnButton } from './ui/button';
import { Card as ShadcnCard, CardContent } from './ui/card';
import { Input as ShadcnInput } from './ui/input';
import {
  Select as ShadcnSelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Table as ShadcnTable, TableBody, TableCell, TableHead, TableHeader } from './ui/table';
import { cn } from '../lib/utils';

export { cn };
export { Tooltip, TooltipProvider } from './ui/tooltip';

/**
 * Adapters over shadcn/ui.
 *
 * The components in ./ui are shadcn's own, unmodified apart from an added success and warning
 * variant. These wrappers keep the prop names the screens were written against — `tone` rather
 * than `variant`, a `Select` that takes plain <option> children — so the whole admin moved onto
 * Radix behaviour without twenty simultaneous rewrites, each of which would have been a chance to
 * change something by accident.
 */

const BUTTON_VARIANT = {
  primary: 'default',
  secondary: 'secondary',
  destructive: 'destructive',
  ghost: 'ghost',
  outline: 'outline',
} as const;

export function Button({
  variant = 'primary',
  ...props
}: Omit<React.ComponentProps<typeof ShadcnButton>, 'variant'> & {
  variant?: keyof typeof BUTTON_VARIANT;
}) {
  return <ShadcnButton variant={BUTTON_VARIANT[variant]} {...props} />;
}

export const Input = ShadcnInput;

const BADGE_VARIANT = {
  muted: 'secondary',
  success: 'success',
  warning: 'warning',
  destructive: 'destructive',
} as const;

export function Badge({
  tone = 'muted',
  children,
  className,
}: {
  tone?: keyof typeof BADGE_VARIANT;
  children: ReactNode;
  className?: string;
}) {
  return (
    <ShadcnBadge variant={BADGE_VARIANT[tone]} {...(className ? { className } : {})}>
      {children}
    </ShadcnBadge>
  );
}

export function Alert({
  tone = 'destructive',
  className,
  children,
}: {
  tone?: 'destructive' | 'warning';
  className?: string;
  children: ReactNode;
}) {
  return (
    <ShadcnAlert variant={tone} {...(className ? { className } : {})}>
      <AlertDescription>{children}</AlertDescription>
    </ShadcnAlert>
  );
}

/** shadcn's Card owns its padding through CardContent; ours took it as a class. */
export function Card({
  className,
  children,
}: {
  className?: string | undefined;
  children: ReactNode;
}) {
  return (
    <ShadcnCard className="gap-0 py-0">
      <CardContent className={cn('p-5', className)}>{children}</CardContent>
    </ShadcnCard>
  );
}

/**
 * A Select that still accepts <option> children.
 *
 * Radix needs SelectItem elements, and every screen here was written against the native element.
 * Reading the children and converting them keeps those call sites intact while the rendered
 * control is the real thing — keyboard navigation, typeahead, portalled menu and all.
 */
export function Select({
  value,
  onChange,
  children,
  disabled,
  className,
  'aria-label': ariaLabel,
}: {
  value: string;
  onChange: (value: string) => void;
  children: ReactNode;
  disabled?: boolean | undefined;
  className?: string | undefined;
  'aria-label'?: string | undefined;
}) {
  const options: Array<{ value: string; label: ReactNode }> = [];

  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    const element = child as ReactElement<{ value?: string | number; children?: ReactNode }>;
    if (element.props.value === undefined) return;
    options.push({ value: String(element.props.value), label: element.props.children });
  });

  const selected = options.find((o) => o.value === value);

  return (
    <ShadcnSelect
      // Radix treats "" as no selection and warns on an empty item value, so the empty option is
      // represented by a sentinel and translated back at the boundary.
      value={value === '' ? '__none__' : value}
      onValueChange={(next) => onChange(next === '__none__' ? '' : next)}
      {...(disabled ? { disabled: true } : {})}
    >
      <SelectTrigger
        className={cn('w-full', className)}
        {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
      >
        <SelectValue>{selected?.label ?? ''}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value === '' ? '__none__' : option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </ShadcnSelect>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
  children: ReactNode;
}) {
  return (
    <label className="block space-y-1.5">
      <span className="text-sm font-medium">{label}</span>
      {children}
      {error ? (
        <span className="block text-xs text-destructive">{error}</span>
      ) : hint ? (
        <span className="block text-xs text-muted-foreground">{hint}</span>
      ) : null}
    </label>
  );
}

export function Table({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-lg border">
      <ShadcnTable>{children}</ShadcnTable>
    </div>
  );
}

export function Th({ children }: { children: ReactNode }) {
  return <TableHead className="text-muted-foreground">{children}</TableHead>;
}

export function Td({ className, children }: { className?: string; children: ReactNode }) {
  return <TableCell className={className}>{children}</TableCell>;
}

export { TableBody, TableHeader };

export function EmptyState({ title, hint }: { title: string; hint?: string | undefined }) {
  return (
    <div className="rounded-lg border border-dashed py-12 text-center">
      <p className="font-medium">{title}</p>
      {hint && <p className="mt-1 text-sm text-muted-foreground">{hint}</p>}
    </div>
  );
}
